import { type MouseEvent as ReactMouseEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { computeOverall, DEFAULT_WEIGHTS, type KpiWeights } from '@rolefit/domain/fit';
import { Badge, Button, Dialog, DialogClose, DialogContent, DialogTitle, Input, Label, Textarea } from '@rolefit/ui';
import { queries, type RoleRow } from '../lib/queries.js';
import { useAsync } from '../lib/useAsync.js';
import { fitTone } from '../lib/fit.js';
import { RoleDrawer } from '../components/RoleDrawer.js';

const isScored = (r: RoleRow): boolean => Object.keys(r.subScores ?? {}).length > 0;

export function PipelinePage() {
  const roles = useAsync(() => queries.listRoles(), []);
  const profile = useAsync(() => queries.getProfile(), []);
  const [openRole, setOpenRole] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [scoring, setScoring] = useState<{ done: number; total: number } | null>(null);
  const [query, setQuery] = useState('');

  const weights: KpiWeights =
    profile.data?.kpi_weights && Object.keys(profile.data.kpi_weights).length
      ? profile.data.kpi_weights
      : DEFAULT_WEIGHTS;

  const unscored = (roles.data ?? []).filter((r) => !isScored(r));

  // Score every unscored role, refetching after each so the table streams in
  // fresh fit numbers instead of waiting for the whole batch.
  async function scoreAll() {
    const todo = (roles.data ?? []).filter((r) => !isScored(r));
    setScoring({ done: 0, total: todo.length });
    let done = 0;
    for (const r of todo) {
      try {
        await queries.scoreRole(r.id);
      } catch {
        // Skip a role that fails to score; the rest still get their numbers.
      }
      done += 1;
      setScoring({ done, total: todo.length });
      roles.refetch();
    }
    setScoring(null);
  }

  const [sort, setSort] = useState<{ key: 'fit' | 'role' | 'company' | 'status'; dir: 1 | -1 }>({
    key: 'fit',
    dir: -1,
  });

  const ranked = useMemo(() => {
    const rows = (roles.data ?? []).map((r) => ({ row: r, overall: computeOverall(r.subScores, weights) }));
    return rows.sort((a, b) => {
      let d = 0;
      if (sort.key === 'fit') d = a.overall - b.overall;
      else if (sort.key === 'role') d = (a.row.title ?? '').localeCompare(b.row.title ?? '');
      else if (sort.key === 'company') d = (a.row.company ?? '').localeCompare(b.row.company ?? '');
      else d = (a.row.status ?? '').localeCompare(b.row.status ?? '');
      return d * sort.dir;
    });
  }, [roles.data, weights, sort]);

  const toggleSort = (key: typeof sort.key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: key === 'fit' ? -1 : 1 }));
  const arrow = (key: typeof sort.key) => (sort.key === key ? (sort.dir === 1 ? ' ↑' : ' ↓') : '');

  // Download the current pipeline as CSV (client-side, from loaded data).
  function exportCsv() {
    const esc = (v: string | number) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [['Fit', 'Role', 'Company', 'Status', 'Package', 'Source URL']];
    for (const { row, overall } of ranked) {
      rows.push([
        isScored(row) ? overall.toFixed(1) : '',
        row.title ?? 'Untitled role',
        row.company ?? '',
        row.status,
        row.has_package ? 'ready' : '',
        row.sourceUrl ?? '',
      ]);
    }
    const csv = rows.map((r) => r.map(esc).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rolefit-pipeline.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  // Client-side filter by role title or company; instant, no refetch.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ranked;
    return ranked.filter(
      ({ row }) => (row.title ?? '').toLowerCase().includes(q) || (row.company ?? '').toLowerCase().includes(q),
    );
  }, [ranked, query]);

  return (
    <div className="mx-auto max-w-5xl p-6">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Pipeline</h1>
          <p className="text-sm text-muted-foreground">Roles ranked by your fit KPI.</p>
        </div>
        <div className="flex items-center gap-2">
          {profile.data?.confirmed && (unscored.length > 0 || scoring) && (
            <Button variant="outline" disabled={scoring !== null} onClick={scoreAll}>
              {scoring ? `Scoring ${scoring.done}/${scoring.total}…` : `Score all (${unscored.length})`}
            </Button>
          )}
          {ranked.length > 0 && (
            <Button variant="outline" onClick={exportCsv}>
              Export
            </Button>
          )}
          <Button onClick={() => setAddOpen(true)}>Add role</Button>
        </div>
      </header>

      {!profile.loading && !profile.data?.confirmed && (
        <div className="mb-6 flex items-center justify-between gap-4 rounded-lg border border-dashed bg-muted/30 p-4">
          <p className="text-sm text-muted-foreground">
            Import and confirm your profile to score roles and generate tailored packages.
          </p>
          <Button asChild size="sm">
            <Link to="/onboard">Set up profile</Link>
          </Button>
        </div>
      )}

      {roles.loading && <p className="text-sm text-muted-foreground">Loading roles…</p>}
      {roles.error && <p className="text-sm text-destructive">{roles.error}</p>}
      {!roles.loading && ranked.length === 0 && (
        <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No roles yet. Add one to get started.
        </p>
      )}

      {ranked.length > 0 && (
        <div className="mb-3 flex items-center gap-3">
          <Input
            type="search"
            placeholder="Filter by role or company…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-9 max-w-xs"
          />
          {query.trim() && (
            <span className="text-xs text-muted-foreground">
              {visible.length} of {ranked.length}
            </span>
          )}
        </div>
      )}

      {ranked.length > 0 && (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                {(
                  [
                    ['fit', 'Fit'],
                    ['role', 'Role'],
                    ['company', 'Company'],
                    ['status', 'Status'],
                  ] as const
                ).map(([key, label]) => (
                  <th key={key} className="px-4 py-2 font-medium">
                    <button type="button" className="hover:text-foreground" onClick={() => toggleSort(key)}>
                      {label}
                      {arrow(key)}
                    </button>
                  </th>
                ))}
                <th className="px-4 py-2 font-medium">Package</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {visible.map(({ row, overall }) => (
                <PipelineRow
                  key={row.id}
                  row={row}
                  overall={overall}
                  onOpen={() => setOpenRole(row.id)}
                  onDeleted={() => roles.refetch()}
                />
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No roles match “{query.trim()}”.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {openRole && (
        <RoleDrawer
          roleId={openRole}
          weights={weights}
          onClose={() => setOpenRole(null)}
          onChanged={() => roles.refetch()}
        />
      )}

      <AddRoleDialog open={addOpen} onOpenChange={setAddOpen} onAdded={() => roles.refetch()} />
    </div>
  );
}

function PipelineRow({
  row,
  overall,
  onOpen,
  onDeleted,
}: {
  row: RoleRow;
  overall: number;
  onOpen: () => void;
  onDeleted: () => void;
}) {
  const tone = fitTone(overall);
  const scored = isScored(row);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [delError, setDelError] = useState<string | null>(null);

  async function remove(e: ReactMouseEvent) {
    e.stopPropagation();
    setDeleting(true);
    setDelError(null);
    try {
      await queries.deleteRole(row.id);
      onDeleted();
    } catch (err) {
      // Keep the confirm row open and surface the failure instead of snapping
      // back silently.
      setDeleting(false);
      setDelError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <tr className="group cursor-pointer border-t hover:bg-accent/50" onClick={onOpen}>
      <td className="px-4 py-3">
        {scored ? (
          <span className={`rounded-md px-2 py-0.5 font-semibold tabular-nums ${tone.chip}`}>{overall.toFixed(1)}</span>
        ) : (
          <span className="text-muted-foreground" title="Not scored yet">
            —
          </span>
        )}
      </td>
      <td className="px-4 py-3 font-medium">{row.title ?? 'Untitled role'}</td>
      <td className="px-4 py-3 text-muted-foreground">{row.company ?? '—'}</td>
      <td className="px-4 py-3">
        <Badge variant="outline" className="capitalize">
          {row.status}
        </Badge>
      </td>
      <td className="px-4 py-3">
        {row.has_package ? <Badge variant="success">ready</Badge> : <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-2 py-3 text-right">
        {confirming ? (
          <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {delError && <span className="text-xs text-destructive">{delError}</span>}
            <button
              type="button"
              disabled={deleting}
              onClick={remove}
              className="rounded px-1.5 py-0.5 text-xs font-medium text-destructive hover:bg-destructive/10"
            >
              {deleting ? 'Deleting…' : delError ? 'Retry' : 'Delete'}
            </button>
            <button
              type="button"
              disabled={deleting}
              onClick={(e) => {
                e.stopPropagation();
                setConfirming(false);
                setDelError(null);
              }}
              className="rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent"
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            aria-label="Delete role"
            title="Delete role"
            onClick={(e) => {
              e.stopPropagation();
              setDelError(null);
              setConfirming(true);
            }}
            className="rounded p-1 text-muted-foreground opacity-0 transition hover:text-destructive group-hover:opacity-100 focus:opacity-100"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" />
            </svg>
          </button>
        )}
      </td>
    </tr>
  );
}

const URL_RE = /^https?:\/\/\S+$/i;

function AddRoleDialog({
  open,
  onOpenChange,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onAdded: () => void;
}) {
  const [input, setInput] = useState('');
  const [showGreenhouse, setShowGreenhouse] = useState(false);
  const [board, setBoard] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  function reset() {
    setInput('');
    setBoard('');
    setShowGreenhouse(false);
    setError(null);
    setNote(null);
  }

  const trimmed = input.trim();
  const isUrl = URL_RE.test(trimmed);

  async function submit() {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      // A single URL is fetched + extracted; anything else is the pasted JD.
      const res = await queries.ingestRole(isUrl ? { source_url: trimmed } : { jd_text: input });
      onAdded();
      // Tell the user when nothing new was added (a duplicate), rather than
      // closing silently; otherwise close on a fresh add.
      if (res.deduped) {
        setNote('That role is already in your pipeline.');
      } else {
        onOpenChange(false);
        reset();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add role');
    } finally {
      setBusy(false);
    }
  }

  async function importBoard() {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const r = await queries.ingestGreenhouse(board.trim());
      onAdded();
      setNote(
        `Imported ${r.ingested} role${r.ingested === 1 ? '' : 's'}${r.deduped ? `, skipped ${r.deduped} duplicate${r.deduped === 1 ? '' : 's'}` : ''}.`,
      );
      setBoard('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogTitle className="text-lg font-semibold">Add a role</DialogTitle>

        <div className="mt-4 space-y-3">
          <Textarea
            rows={6}
            autoFocus
            placeholder="Paste a job posting URL, or the full job description…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {trimmed === ''
              ? 'A link (we fetch the posting) or the pasted description — either works.'
              : isUrl
                ? "Looks like a URL — we'll fetch and extract the posting."
                : 'Treating this as a pasted job description.'}
          </p>

          {error && <p className="text-sm text-destructive">{error}</p>}
          {note && <p className="text-sm text-emerald-600 dark:text-emerald-400">{note}</p>}

          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="ghost">Close</Button>
            </DialogClose>
            <Button disabled={busy || !trimmed} onClick={submit}>
              {busy ? 'Working…' : 'Add role'}
            </Button>
          </div>

          {/* Secondary: bulk-import a whole Greenhouse board. */}
          <div className="border-t pt-3">
            {!showGreenhouse ? (
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setShowGreenhouse(true)}
              >
                Bulk-import a Greenhouse board
              </button>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="board" className="text-xs">
                  Greenhouse board token (the slug in boards.greenhouse.io/&lt;token&gt;)
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="board"
                    placeholder="e.g. stripe"
                    value={board}
                    onChange={(e) => setBoard(e.target.value)}
                    className="h-8 text-sm"
                  />
                  <Button size="sm" variant="outline" disabled={busy || !board.trim()} onClick={importBoard}>
                    Import
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
